// Reserved, page-independent sibling routes under /trade (see below) — excluded here
// so neither is ever mistaken for a /trade/[pageId] page id.
const RESERVED_TRADE_SUBROUTES = new Set([
  'home',
  'risk-slides',
  'hedge',
  'screener',
  'arbitrage',
  'options-mm',
  'assistant',
  'rfq',
])

// Shallow (client-side) page switching is only valid while the /trade/[pageId]
// route itself is mounted — /trade and deeper paths must not match.
const TRADE_PAGE_PATH_RE = /^\/trade\/([^/?#]+)$/

export function getTradePageIdFromPath(pathname: string): string | null {
  const id = TRADE_PAGE_PATH_RE.exec(pathname)?.[1] ?? null
  return id && !RESERVED_TRADE_SUBROUTES.has(id) ? id : null
}

export function getTradePagePath(pageId: string): string {
  return `/trade/${pageId}`
}

// Home (see home-page.tsx): the hub that summarizes what is going on across every account and
// page, and points to the rest of the product (AI, hedging, arbitrage, risk, screener). Lives
// under /trade so it gets the same account/order syncs and assistant hosts as every trade route.
const HOME_PAGE_PATH = '/trade/home'

export function getHomePagePath(): string {
  return HOME_PAGE_PATH
}

export function isHomePagePath(pathname: string): boolean {
  return pathname === HOME_PAGE_PATH
}

// The full-page Risk Slides view (see risk-slides-page.tsx) — a real route rather
// than a dialog/overlay, so the browser's back/forward and refresh all work on it
// normally. Page-independent: the account-wide risk simulation + positions it shows
// are the same regardless of which trade page you opened it from.
const RISK_SLIDES_PAGE_PATH = '/trade/risk-slides'

export function getRiskSlidesPagePath(): string {
  return RISK_SLIDES_PAGE_PATH
}

export function isRiskSlidesPagePath(pathname: string): boolean {
  return pathname === RISK_SLIDES_PAGE_PATH
}

// The full-page Delta Hedge view (see hedge-page.tsx) — same reasoning as Risk
// Slides above: a real, page-independent route instead of the hedge builder dialog.
const HEDGE_PAGE_PATH = '/trade/hedge'

export function getHedgePagePath(): string {
  return HEDGE_PAGE_PATH
}

export function isHedgePagePath(pathname: string): boolean {
  return pathname === HEDGE_PAGE_PATH
}

// The full-page Market Screener (see screener-page.tsx) — a market-wide view, the
// same whichever trade page you opened it from.
const SCREENER_PAGE_PATH = '/trade/screener'

export function getScreenerPagePath(): string {
  return SCREENER_PAGE_PATH
}

export function isScreenerPagePath(pathname: string): boolean {
  return pathname === SCREENER_PAGE_PATH
}

// The full-page Arbitrage view (see arbitrage-page.tsx) — the cross-exchange arb builder
// as a real route, same reasoning as Delta Hedge above.
const ARBITRAGE_PAGE_PATH = '/trade/arbitrage'

export function getArbitragePagePath(): string {
  return ARBITRAGE_PAGE_PATH
}

export function isArbitragePagePath(pathname: string): boolean {
  return pathname === ARBITRAGE_PAGE_PATH
}

// The full-page Options MM view (see options-mm-page.tsx) — start and watch market-maker
// instances, same reasoning as Delta Hedge above.
const OPTIONS_MM_PAGE_PATH = '/trade/options-mm'

export function getOptionsMmPagePath(): string {
  return OPTIONS_MM_PAGE_PATH
}

export function isOptionsMmPagePath(pathname: string): boolean {
  return pathname === OPTIONS_MM_PAGE_PATH
}

// The full-page assistant (see assistant-page.tsx) — the same surface and thread as the docked
// assistant panel, given the whole width plus its recent list.
const ASSISTANT_PAGE_PATH = '/trade/assistant'

export function getAssistantPagePath(): string {
  return ASSISTANT_PAGE_PATH
}

export function isAssistantPagePath(pathname: string): boolean {
  return pathname === ASSISTANT_PAGE_PATH
}

// The full-page maker RFQ blotter (see rfq-maker-page.tsx): live RFQs from other takers across
// every maker venue, the quote ticket and this user's quotes. Page-independent, like Arbitrage.
const RFQ_MAKER_PAGE_PATH = '/trade/rfq'

export function getRfqMakerPagePath(): string {
  return RFQ_MAKER_PAGE_PATH
}

export function isRfqMakerPagePath(pathname: string): boolean {
  return pathname === RFQ_MAKER_PAGE_PATH
}
